import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

// The client is created on first use rather than at module load (CS-308), so
// modules that import `supabase` can be loaded (e.g. by the unit test suite)
// without VITE_SUPABASE_* set. The env check still throws, just at first use.
function readSupabaseConfig(): { url: string; anonKey: string } {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!SUPABASE_URL) {
    throw new Error('Missing VITE_SUPABASE_URL environment variable');
  }

  if (!SUPABASE_ANON_KEY) {
    throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
  }

  return { url: SUPABASE_URL, anonKey: SUPABASE_ANON_KEY };
}

// GoTrue assigns its internal logger to the browser logging API; debug:false
// ensures logDebugMessages stays off so the logger is never invoked.
const createBaseClient = (SUPABASE_URL: string, SUPABASE_ANON_KEY: string) => createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true, // CS-108: background refresh; ensureFreshAuth remains the fallback
    persistSession: true,
    detectSessionInUrl: true,
    debug: false,
  },
});

// Auth-only timeout guard (safe: these return Promises, no builder chaining here)
const AUTH_TIMEOUT_MS = 8000; // CS-108: 2s was too short for a refresh on slow wifi
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timeout`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  }) as Promise<T>;
}

// Build a Proxy over auth that only wraps specific methods, preserving all others
function wrapAuth(originalAuth: SupabaseClient['auth']): SupabaseClient['auth'] {
  return new Proxy(originalAuth, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (prop === 'getSession' || prop === 'refreshSession' || prop === 'getUser') {
        if (typeof value === 'function') {
          return (...args: unknown[]) =>
            withTimeout(
              (value as (...fnArgs: unknown[]) => unknown).apply(target, args as unknown[]),
              AUTH_TIMEOUT_MS,
              `auth.${String(prop)}`
            );
        }
      }
      // For all other functions, preserve correct this-binding
      if (typeof value === 'function') {
        return (value as (...fnArgs: unknown[]) => unknown).bind(target);
      }
      return value;
    }
  }) as SupabaseClient['auth'];
}

type AppSupabaseClient = ReturnType<typeof createBaseClient>;

let clientInstance: AppSupabaseClient | null = null;

/** Returns the singleton browser client, creating it on first call. */
export function getSupabaseClient(): AppSupabaseClient {
  if (!clientInstance) {
    const { url, anonKey } = readSupabaseConfig();
    const baseClient = createBaseClient(url, anonKey);
    // Mutate the client auth reference to use our proxy
    (baseClient as unknown as { auth: SupabaseClient['auth'] }).auth = wrapAuth(baseClient.auth);
    clientInstance = baseClient;
  }
  return clientInstance;
}

// Export client with auth-only timeout wrapper; PostgREST builder unchanged.
// Lazy: a Proxy that creates the real client on first property access, so the
// public API (`supabase.from(...)`, `supabase.auth...`, etc.) is unchanged.
export const supabase: AppSupabaseClient = new Proxy({} as AppSupabaseClient, {
  get(_target, prop) {
    const client = getSupabaseClient();
    const value = Reflect.get(client, prop, client);
    return typeof value === 'function' ? value.bind(client) : value;
  },
  set(_target, prop, value) {
    return Reflect.set(getSupabaseClient(), prop, value);
  },
  has(_target, prop) {
    return Reflect.has(getSupabaseClient(), prop);
  },
});
